import { languageDisplayLabel, LanguagesEnum } from '@/api/enums';
import { handleAxiosError, isIn } from '@/api/helpers';
import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import { client } from '@/client';
import {
  ContentSeries,
  contentSeriesTagColors,
  Course,
  CourseFormat,
  CourseStatus,
  HighlightedCourse,
  MaterialType,
} from '@/client/courses';
import { FilterNamesEnum } from '@/client/helpers';
import { Actions, Subjects, SystemRoles } from '@/client/users';
import {
  RedirectPaths,
  RedirectPathsEnum,
  TableNamesEnum,
} from '@/common/constants';
import { DialogContext } from '@/common/context';
import { TranslationFunctionType } from '@/common/types';
import { CourseCloneStatusStack } from '@/components/courses/CourseCloneStatusStack';
import { HighlightedCourseCard } from '@/components/courses/HighlightedCourseCard';
import { CloneCourseModal } from '@/components/courses/modals/CloneCourseModal';
import {
  DataTable,
  DataTableActions,
  DataTableColumnsMultiselect,
  DataTableColumnType,
  DataTableFilters,
  DataTableToolbar,
  FilterType,
  FilterTypeEnum,
} from '@/components/tables/crud';
import { useCourses, useDeleteCourse, useSaveCourse } from '@/hooks/query';
import { useAppSelector } from '@/hooks/store';
import { useTable } from '@/hooks/table.hook';
import { useCourseClone } from '@/hooks/useCourseClone';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { usePermission } from '@/hooks/usePermission';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users';
import { DateFormats, DialogType } from '@/system-settings/enums';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { AppButton } from '@/ui/buttons';
import { AppChip } from '@/ui/chip';
import DatatableThumbnail from '@/ui/datatable-thumbnail/DatatableThumbnail';
import { FormatDate } from '@/ui/date';
import { FlexContainer } from '@/ui/styled-ui';
import { formatDate } from '@/utils/date/date.helper';
import {
  branchAdminCheck,
  CloneIcon,
  CustomizableIcon,
  formatTags,
  isDemoRestrictedMode,
  updateMaterialByTypePath,
} from '@/utils/helpers';
import { PostHogFeatureFlags } from '@/utils/posthog';
import { AxiosError } from 'axios';
import { debounce } from 'lodash';
import moment from 'moment';
import { DataTableRowClickEvent } from 'primereact/datatable';
import { InputText } from 'primereact/inputtext';
import { MenuItem } from 'primereact/menuitem';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tooltip } from 'primereact/tooltip';
import React, {
  FormEvent,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const StyledH2 = styled.h2`
  font-size: var(--semi-big-font-size);
  line-height: var(--semi-big-line-height);
  font-weight: 600;
`;

const StyledFlexContainer = styled(FlexContainer)`
  margin-bottom: var(--big-padding);
`;

const StyledNewLabel = styled.span`
  font-size: var(--xxsmall-font-size);
  font-weight: 500;
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-color: var(--blue-opacity);
  text-decoration-thickness: 13.5%;
  text-underline-offset: 25%;
  text-underline-position: from-font;
  text-transform: uppercase;
`;

export const CoursesPage = () => {
  const { t } = useTranslation();
  const { can } = usePermission();
  const toast = useToast();
  const account = useAppSelector(selectCurrentAccount);
  const currentUser = useAppSelector(selectCurrentUser);
  const isAdminUser = currentUser?.role.code === SystemRoles.ADMIN;
  const isDemoAccount = isDemoRestrictedMode(account, currentUser);
  const isBranchAdmin = branchAdminCheck(currentUser, account);
  const { setDialogData } = useContext(DialogContext);
  const {
    dialogCourse: cloneDialogCourse,
    cloningCourseIds,
    cloneJobs,
    openCloneDialog,
    closeCloneDialog,
    submitClone,
    removeCloneJob,
  } = useCourseClone();
  const deleteCourse = useDeleteCourse();
  const navigate = useNavigate();
  const location = useLocation();
  const { canUseFeature } = useFeatureFlag();

  const { skip, take, sort, apiFilters, onSort, setSkip, onPage, onFilter } =
    useTable({
      defaultSort: ['created', 'asc'],
      tableName: TableNamesEnum.COURSES_LIST,
    });

  const [multiSearchValue, setMultiSearchValue] = useState('');
  const debouncedSetMultiSearchValue = useMemo(
    () =>
      debounce((event: FormEvent) => {
        setSkip(0);
        setMultiSearchValue((event.target as HTMLInputElement).value);
      }, 500),
    [],
  );

  const {
    isLoading,
    courses,
    refetch: refetchCourses,
  } = useCourses({
    accountId: account?.isSystem ? undefined : (account?.id as string),
    take,
    skip,
    search: multiSearchValue,
    filters: [
      ...apiFilters,
      isIn('type', [MaterialType.COURSE, MaterialType.CUSTOM_MATERIAL]),
    ],
    sort: sort && sort.length > 0 ? [sort.join(',')] : [],
  });

  const handleDeleteCourse = async (course: Course) => {
    if (isDemoAccount) return;
    try {
      await deleteCourse.delete(course.id);
      toast?.success(t('toast.success'), t('content.deleted'));
      await refetchCourses();
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  const menuItems = (course: Course) => {
    const menu: MenuItem[] = [
      {
        label: t('course.view.content'),
        icon: 'pi pi-pencil',
        command: () => {
          navigate(
            RedirectPaths[RedirectPathsEnum.COURSES_SCORM_PREVIEW](course.id),
          );
        },
      },
    ];

    if (account?.isSystem) {
      menu.push({
        label: t('course.view.schedule'),
        icon: 'pi pi-calendar',
        command: () => {
          navigate(
            RedirectPaths[RedirectPathsEnum.COURSES_SCHEDULE](course.id),
          );
        },
      });

      if (can(Actions.READ, Subjects.COURSE_ACCOUNT_SCHEDULES)) {
        menu.push({
          label: t('course.view.accounts'),
          icon: 'pi pi-building',
          command: () => {
            navigate(
              RedirectPaths[RedirectPathsEnum.COURSES_ACCOUNTS](course.id),
            );
          },
        });
      }
    }

    if (
      can(Actions.READ, Subjects.COURSE_USER_SCHEDULES) &&
      !account?.isSystem
    ) {
      menu.push({
        label: t('course.view.users'),
        icon: 'pi pi-user',
        command: () => {
          navigate(RedirectPaths[RedirectPathsEnum.COURSES_USERS](course.id));
        },
      });
    }

    if (course?.cloneSettings?.isCloneable) {
      menu.push({
        label: t('content.clone'),
        icon: <CloneIcon />,
        command: async () => {
          if (isAdminUser && account?.id) {
            await submitClone(account.id, account.name, course);
          } else {
            openCloneDialog(course);
          }
        },
      });
    }

    const isCourseAiCustomized =
      !!course?.customizationMetadata?.originalContentId;
    const isCourseCloned = !!course?.cloneMetadata?.isClone;

    if (
      !account?.isSystem &&
      course?.aiCustomizationSettings?.isEnabled &&
      !isCourseAiCustomized &&
      !isCourseCloned
    ) {
      menu.push({
        label: t('content.customizeWithAi'),
        icon: <CustomizableIcon />,
        command: () => {
          navigate(
            RedirectPaths[RedirectPathsEnum.COURSES_CUSTOMIZE](course.id),
          );
        },
      });
    }

    if (canUseFeature(PostHogFeatureFlags.EXPORT_TO_LMS)) {
      if (course.hasScormContent) {
        const disabledClass = `scorm-export-disabled-${course.id}`;
        menu.push({
          label: t('courses.export.to.lms'),
          template: (
            _item: MenuItem,
            options: {
              className: string;
              iconClassName: string;
              labelClassName: string;
            },
          ) => (
            <>
              <span
                className={`${options.className} ${disabledClass}`}
                style={{
                  cursor: 'not-allowed',
                  opacity: 0.5,
                  pointerEvents: 'all',
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <span
                  className={`pi pi-download ${options.iconClassName}`}
                  style={{ marginRight: '0.5rem' }}
                />
                <span className={options.labelClassName}>
                  {t('courses.export.to.lms')}
                </span>
              </span>
              <Tooltip
                target={`.${disabledClass}`}
                content={t('courses.export.to.lms.scorm.not.supported')}
                position="left"
              />
            </>
          ),
        });
      } else {
        menu.push({
          label: t('courses.export.to.lms'),
          icon: 'pi pi-download',
          command: async () => {
            try {
              const response = await client.courses.exportCourseAsScorm(
                course.id,
                course.defaultLanguage ?? LanguagesEnum.EN,
                account?.id as string,
              );
              window.open(
                response.downloadUrl,
                '_blank',
                'noopener,noreferrer',
              );
            } catch (error) {
              handleAxiosError(error as Error | AxiosError, toast);
            }
          },
        });
      }
    }

    const isPublished = course.status === CourseStatus.PUBLISHED;
    const isCustomCourse = !!course.account;
    const isSystemView = !!account?.isSystem;
    const canDeleteInAccountContext = isSystemView || isCustomCourse;
    const canDeleteByStatus = !isPublished || isCustomCourse;

    const canDelete =
      canDeleteByStatus &&
      !isBranchAdmin &&
      can(Actions.DELETE, Subjects.COURSES) &&
      canDeleteInAccountContext;

    if (canDelete) {
      menu.push({
        label: t('generic.delete'),
        icon: 'pi pi-trash',
        command: isDemoAccount
          ? undefined
          : () =>
              setDialogData({
                type: DialogType.CONFIRMATION,
                show: true,
                header: t('dialog.delete.confirm'),
                message: t('dialog.delete.content'),
                onAccept: async () => handleDeleteCourse(course),
              }),
      });
    }

    return menu;
  };

  const columns: DataTableColumnType[] = [
    {
      field: 'name',
      header: t('content'),
      sortable: true,
      filterable: false,
      render: (row: Course) => {
        return (
          <FlexContainer gap={12}>
            {row?.courseThumbnail?.signedUrl && (
              <DatatableThumbnail
                className={
                  moment(row?.releaseDate).isAfter(moment(), 'day')
                    ? 'upcoming-course'
                    : ''
                }
                url={row?.courseThumbnail?.signedUrl}
              />
            )}
            <FlexContainer
              direction="column"
              align="center"
              className="align-self-center"
            >
              <span>
                {row?.releaseDate &&
                  moment(row?.releaseDate).isAfter(
                    moment().subtract(6, 'months'),
                    'day',
                  ) &&
                  !moment(row?.releaseDate).isAfter(moment(), 'day') && (
                    <StyledNewLabel>{t('generic.new')}</StyledNewLabel>
                  )}
                <FlexContainer gap={4}>
                  <span className="mr-2">{row?.name}</span>
                  {row?.account && (
                    <AppChip label={t('generic.custom')} type="red" size="s" />
                  )}

                  {row?.contentSeries && (
                    <AppChip
                      label={t(`content.series.${row?.contentSeries}`)}
                      type={contentSeriesTagColors[row?.contentSeries]}
                      size="s"
                    />
                  )}
                </FlexContainer>
              </span>

              {!!row?.tags?.length && (
                <span className="secondary-text">
                  <span>{t('content.tags')}: </span>
                  {formatTags(row?.tags)}
                </span>
              )}
            </FlexContainer>
          </FlexContainer>
        );
      },
    },
    {
      field: 'status',
      header: t('generic.status'),
      sortable: false,
      filterable: false,
      render: (row: Course) => {
        return row?.releaseDate &&
          moment(row?.releaseDate).isAfter(moment(), 'day') ? (
          <FlexContainer
            direction="column"
            justify="flex-start"
            gap={9}
            className="min-w-max"
          >
            <AppChip
              label={t('content.upcoming')}
              type="secondary"
              size="s"
              tooltip={formatDate(row.releaseDate, DateFormats.DATEONLY)}
            />
            {row?.isPreviewable && (
              <AppChip
                label={t('course.preview.ready')}
                type="secondary"
                size="s"
              />
            )}
          </FlexContainer>
        ) : (
          <AppChip label={t('generic.available')} type="primary" size="s" />
        );
      },
    },
  ];

  columns.push({
    field: 'cloneSettings.isCloneable',
    header: t('content.customizable'),
    sortable: false,
    filterable: false,
    render: (row: Course) => {
      const showClone = !!row?.cloneSettings?.isCloneable;
      const showAi = !!row?.aiCustomizationSettings?.isEnabled;

      if (!showClone && !showAi) return null;

      return (
        <FlexContainer gap={6} justify="flex-start">
          {showClone && (
            <>
              <span className={`clonable-icon-${row.id} pt-2 d-inline-flex`}>
                <CloneIcon />
              </span>
              <Tooltip
                position="top"
                target={`.clonable-icon-${row.id}`}
                content={t('content.clonable.tooltip')}
              />
            </>
          )}
          {showAi && (
            <>
              <span
                className={`ai-customizable-icon-${row.id} pt-2 d-inline-flex`}
              >
                <CustomizableIcon />
              </span>
              <Tooltip
                position="top"
                target={`.ai-customizable-icon-${row.id}`}
                content={t('content.aiCustomizable.tooltip')}
              />
            </>
          )}
        </FlexContainer>
      );
    },
  });

  if (!account?.isSystem) {
    columns.push({
      field: 'totalNumberOfEnrollments',
      header: t('courses.statistics.total_enrollments'),
      sortable: true,
      filterable: false,
      render: (row: Course) => (
        <span>{row.totalNumberOfEnrollments || '—'}</span>
      ),
    });

    columns.push({
      field: 'completionPercentage',
      header: t('courses.statistics.completion_percentage'),
      sortable: true,
      filterable: false,
      render: (row: Course) => (
        <span>
          {row.completionPercentage ? `${row.completionPercentage}%` : '—'}
        </span>
      ),
    });
  }

  if (account?.isSystem) {
    columns.push({
      field: 'courseLastUpdatedAt',
      header: t('generic.last.updated'),
      sortable: false,
      filterable: false,
      render: (row: Course) =>
        row?.courseLastUpdatedAt ? (
          <FormatDate date={row?.courseLastUpdatedAt} />
        ) : (
          t('generic.never')
        ),
    });

    columns.push({
      field: 'customContent',
      header: t('content.custom.content'),
      sortable: false,
      filterable: false,
      render: (row: Course) =>
        row?.account ? (
          <AppChip label={t('dialog.yes')} type="primary" size="s" />
        ) : (
          <AppChip label={t('dialog.no')} type="secondary" size="s" />
        ),
    });
  }

  columns.push({
    field: 'actions',
    header: t('generic.actions'),
    sortable: false,
    filterable: false,
    style: {
      width: '80px',
      textAlign: 'center',
    },
    render: (row: Course) =>
      cloningCourseIds.includes(row.id) ? (
        <FlexContainer>
          <ProgressSpinner style={{ width: '24px', height: '24px' }} />
        </FlexContainer>
      ) : (
        <DataTableActions
          disabled={menuItems(row).length < 1}
          menuItems={menuItems(row)}
        />
      ),
  });

  // Set the preselected columns
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  const defaultVisibleColumns = columns
    .filter((column) => column.field !== 'id')
    .map((column) => column.field);
  const alwaysVisibleColumns = ['name', 'actions'];
  //

  const filters = (t: TranslationFunctionType): FilterType[] => [
    {
      label: t('content.custom.content'),
      field: FilterNamesEnum.COURSE_CUSTOM_CONTENT,
      placeholder: t('content.custom.content'),
      type: FilterTypeEnum.SELECT_YES_NO,
    },
    {
      label: t('content.tags'),
      field: FilterNamesEnum.COURSE_TAGS,
      placeholder: t('content.tags'),
      type: FilterTypeEnum.MULTI_SELECT,
    },
    {
      label: t('content.series'),
      field: FilterNamesEnum.COURSE_SERIES,
      placeholder: t('content.series'),
      type: FilterTypeEnum.MULTI_SELECT,
      options: Object.values(ContentSeries)
        .map((series) => ({
          label: t(`content.series.${series}`),
          value: series,
        }))
        .concat({
          label: '',
          value: null as unknown as ContentSeries,
        }),
    },
    {
      label: t('generic.lang'),
      field: FilterNamesEnum.COURSE_LANGUAGES,
      placeholder: t('generic.lang'),
      type: FilterTypeEnum.MULTI_SELECT,
      options: Object.values(LanguagesEnum).map((language) => ({
        label: languageDisplayLabel[language],
        value: language,
      })),
    },
    {
      label: t('content.clonable'),
      field: FilterNamesEnum.COURSE_CLONABLE,
      placeholder: t('content.clonable'),
      type: FilterTypeEnum.SELECT_YES_NO,
    },
    {
      label: t('content.aiCustomizable'),
      field: FilterNamesEnum.COURSE_AI_CUSTOMIZABLE,
      placeholder: t('content.aiCustomizable'),
      type: FilterTypeEnum.SELECT_YES_NO,
    },
  ];

  const toolbar = (
    <DataTableToolbar>
      <FlexContainer
        justify="space-between"
        gap={8}
        align="flex-start"
        wrap="wrap"
      >
        {!!filters(t).length && (
          <DataTableFilters
            filters={filters(t)}
            onFilter={onFilter}
            tableName={TableNamesEnum.COURSES_LIST}
            defaultValues={{
              languages: [],
              contentSeries: [
                ContentSeries.ESSENTIALS,
                ContentSeries.STORIES,
                null,
              ],
            }}
            className="flex-initial"
          />
        )}
        <div className="p-input-icon-left flex-auto min-w-300">
          <InputText
            className="w-full"
            onInput={debouncedSetMultiSearchValue}
            placeholder={t('content.search.name')}
            autoComplete="off"
          />
          <i className="pi pi-search" />
        </div>
      </FlexContainer>
      <DataTableColumnsMultiselect
        columns={columns}
        tableName={TableNamesEnum.COURSES_LIST}
        visibleColumns={visibleColumns}
        setVisibleColumns={setVisibleColumns}
        defaultVisibleColumns={defaultVisibleColumns}
        alwaysVisibleColumns={alwaysVisibleColumns}
      />
    </DataTableToolbar>
  );

  const pathItems = [
    {
      label: account?.name,
      url: !account?.isSystem
        ? RedirectPaths[RedirectPathsEnum.ACCOUNT](account?.id as string)
        : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](account?.id),
      template: AppBreadCrumbTemplate,
    },
    {
      label: t('content'),
      url: RedirectPaths[RedirectPathsEnum.COURSES](),
      template: AppBreadCrumbTemplate,
    },
  ];

  const onRowClick = (e: DataTableRowClickEvent) => {
    const locationState = {
      state: { from: location.pathname + location.search },
    };

    if (e.data?.type === MaterialType.CUSTOM_MATERIAL) {
      return navigate(
        updateMaterialByTypePath(e.data.type, e.data.id),
        locationState,
      );
    }

    return !e.data?.releaseDate ||
      e.data?.isPreviewable ||
      moment(e.data?.releaseDate).isBefore(moment(), 'day') ||
      currentUser?.account.isSystem
      ? navigate(
          RedirectPaths[RedirectPathsEnum.COURSES_SCORM_PREVIEW](e.data.id),
          locationState,
        )
      : navigate(
          RedirectPaths[RedirectPathsEnum.COURSES_CONTENT](e.data.id),
          locationState,
        );
  };

  const [highlightedCourses, setHighlightedCourses] = useState<
    HighlightedCourse[]
  >([]);
  const [isHighlightedCoursesLoading, setIsHighlightedCoursesLoading] =
    useState(false);

  useEffect(() => {
    const fetchHighlightedCourses = async () => {
      if (!account?.id) return;

      try {
        setIsHighlightedCoursesLoading(true);
        const highlightedCourses = await client.courses.getHighlightedCourses(
          account?.id,
        );
        setHighlightedCourses(highlightedCourses);
      } catch (e) {
        handleAxiosError(e as Error | AxiosError, toast);
      } finally {
        setIsHighlightedCoursesLoading(false);
      }
    };

    fetchHighlightedCourses();
  }, []);

  const createCourse = useSaveCourse();
  const handleCreateCourse = async () => {
    const response = await createCourse.create({
      format: CourseFormat.CONTENTS,
      type: MaterialType.COURSE,
      courseTranslations: [
        {
          name: t('content.newContent'),
          language: LanguagesEnum.EN,
          description: '',
        },
      ],
      courseLanguages: [LanguagesEnum.EN],
      account: !account?.isSystem && account?.id ? account.id : null,
      courseFiles: [],
      releaseDate: null,
      plannerScheduleDate: null,
      isPreviewable: false,
    });
    navigate(RedirectPaths[RedirectPathsEnum.COURSES_CREATE](response.id));
  };

  return (
    <>
      <AppBreadCrumb model={pathItems} />
      <FlexContainer justify="space-between" className="mb-4">
        <h1 className="mb-0">{t('content')}</h1>
        {can(Actions.CREATE, Subjects.COURSES) &&
          (account?.isSystem || !isBranchAdmin) && (
            <AppButton
              label={t('button.createNew')}
              severity="secondary"
              onClick={isDemoAccount ? undefined : handleCreateCourse}
            />
          )}
      </FlexContainer>

      {isHighlightedCoursesLoading ? (
        <FlexContainer direction="column" className="mt-5">
          <ProgressSpinner />
        </FlexContainer>
      ) : (
        <>
          {!!highlightedCourses?.length && (
            <>
              <StyledH2>{t('courses.highlighted')}</StyledH2>
              <StyledFlexContainer
                direction="row"
                align="initial"
                justify="flex-start"
                gap={14}
                width="100%"
                className="highlighted-content"
              >
                {highlightedCourses?.slice(0, 4).map((course, index) => (
                  <HighlightedCourseCard
                    key={course.id}
                    id={course.id}
                    course={course}
                    index={index}
                  />
                ))}
              </StyledFlexContainer>
            </>
          )}

          <DataTable
            data={courses?.result}
            count={courses?.count as number}
            isLoading={isLoading}
            toolbar={toolbar}
            columns={columns}
            visibleColumns={visibleColumns}
            onPage={onPage}
            rows={take}
            skip={skip}
            onSort={onSort}
            sort={sort}
            tableClassName="content-table"
            onRowClick={onRowClick}
          />
        </>
      )}

      <CloneCourseModal
        visible={!!cloneDialogCourse}
        course={cloneDialogCourse}
        currentAccount={account}
        isCloning={
          !!cloneDialogCourse && cloningCourseIds.includes(cloneDialogCourse.id)
        }
        onHide={closeCloneDialog}
        onClone={submitClone}
      />

      <CourseCloneStatusStack jobs={cloneJobs} onRemove={removeCloneJob} />
    </>
  );
};
